/**
 * Public (unauthenticated) reads for the Business homepage.
 *
 * The Business admin (`/business-admin?tab=homepage`) writes homepage sections
 * and partner banners; this is the read side used by `/business` so the page
 * content is live-editable instead of hardcoded.
 */
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

/**
 * Anonymous Data API client. New-format `sb_publishable_*` keys are opaque, not
 * JWTs — PostgREST rejects them in the Authorization header, so send `apikey`
 * only.
 */
function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient(process.env["SUPABASE_URL"]!, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export type BusinessHomeSection = {
  id: string;
  key: string;
  title_en: string;
  title_fi: string | null;
  subtitle_en: string | null;
  subtitle_fi: string | null;
  sort_order: number;
};

export type BusinessPartnerBanner = {
  id: string;
  partner_name: string;
  logo_url: string | null;
  banner_image_url: string | null;
  banner_image_url_mobile: string | null;
  headline_en: string | null;
  headline_fi: string | null;
  description_en: string | null;
  description_fi: string | null;
  cta_label_en: string | null;
  cta_label_fi: string | null;
  cta_url: string | null;
  category: string | null;
  location_label: string | null;
  is_sponsored: boolean;
  priority: number;
};

export type BusinessCategory = {
  id: string;
  key: string;
  name_en: string;
  name_fi: string | null;
  icon: string;
  icon_url?: string | null;
  image_url?: string | null;
  sort_order: number;
};

export type BusinessListingPublic = {
  id: string;
  name: string | null;
  summary_en: string | null;
  summary_fi: string | null;
  description_en: string | null;
  description_fi: string | null;
  address: string | null;
  lat?: number | null;
  lng?: number | null;
  destinations: string[];
  tags: string[];
  images: any;
  links: any;
  contact: any;
  is_featured: boolean;
  sort_order: number;
  /** Taxonomy category ids assigned in Business admin → Categories & attributes. */
  category_ids?: string[];
};

export type BusinessServicePublic = {
  id: string;
  category_id: string | null;
  name_en: string;
  name_fi: string | null;
  description_en: string | null;
  description_fi: string | null;
  image_url: string | null;
  destinations: string[];
  links: any;
  is_featured: boolean;
};

export type BusinessEventPublic = {
  id: string;
  title_en: string;
  title_fi: string | null;
  description_en: string | null;
  description_fi: string | null;
  image_url: string | null;
  venue_name: string | null;
  destination: string | null;
  starts_at: string | null;
  ends_at: string | null;
  external_url: string | null;
  registration_url: string | null;
  price_text: string | null;
  is_featured: boolean;
  category_id: string | null;
};

export const businessGetPublicHome = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();

  const nowIso = new Date().toISOString();

  const [sections, copy, banners, categories, listings, services, events, lanePickRows] = await Promise.all([
    supabase
      .from("business_home_sections")
      .select("id,key,title_en,title_fi,subtitle_en,subtitle_fi,sort_order,is_active")
      .order("sort_order", { ascending: true }),
    supabase.from("site_settings").select("value").eq("key", "business_home_copy").maybeSingle(),
    supabase
      .from("business_partner_banners")
      .select(
        "id,partner_name,logo_url,banner_image_url,banner_image_url_mobile,headline_en,headline_fi,description_en,description_fi,cta_label_en,cta_label_fi,cta_url,category,location_label,is_sponsored,priority,start_date,end_date",
      )
      .eq("is_active", true)
      .order("priority", { ascending: false })
      .order("created_at", { ascending: true }),
    supabase
      .from("business_categories")
      .select("id,key,name_en,name_fi,icon,icon_url,image_url,sort_order")
      .eq("is_active", true)
      .is("archived_at", null)
      .order("sort_order", { ascending: true }),
    supabase
      .from("business_listings")
      .select(
        "id,name,summary_en,summary_fi,description_en,description_fi,address,lat,lng,destinations,tags,images,links,contact,is_featured,sort_order,place:places(image_url),categories:business_listing_categories(category_id)",
      )
      .eq("status", "published")
      .order("is_featured", { ascending: false })
      .order("sort_order", { ascending: true })
      .limit(60),
    supabase
      .from("business_services")
      .select(
        "id,category_id,name_en,name_fi,description_en,description_fi,image_url,destinations,links,is_featured",
      )
      .eq("status", "published")
      .order("is_featured", { ascending: false })
      .order("sort_order", { ascending: true })
      .limit(30),
    supabase
      .from("business_events")
      .select(
        "id,title_en,title_fi,description_en,description_fi,image_url,venue_name,destination,starts_at,ends_at,external_url,registration_url,price_text,is_featured,category_id",
      )
      .eq("status", "published")
      .is("archived_at", null)
      .or(`ends_at.gte.${nowIso},ends_at.is.null`)
      .order("starts_at", { ascending: true })
      .limit(12),
    supabase
      .from("business_lane_picks")
      .select("lane_key,sort_order,place:places(id,slug,published)")
      .order("sort_order", { ascending: true }),
  ]);

  const liveBanners = (banners.data ?? []).filter((b: any) => {
    if (b.start_date && b.start_date > nowIso) return false;
    if (b.end_date && b.end_date < nowIso) return false;
    return true;
  });

  // Curated lane picks: lane key -> ordered list of place keys (slug or id),
  // matching the identifier used by the public places feed.
  const lanePicks: Record<string, string[]> = {};
  for (const row of (lanePickRows.data ?? []) as any[]) {
    const place = row.place;
    if (!place || place.published === false) continue;
    const key = place.slug || place.id;
    if (!key) continue;
    (lanePicks[row.lane_key] ??= []).push(key);
  }

  return {
    sections: ((sections.data ?? []) as any[]).filter((s) => s.is_active !== false) as BusinessHomeSection[],
    // Section keys an admin switched off in Business admin → Homepage.
    hiddenSections: ((sections.data ?? []) as any[])
      .filter((s) => s.is_active === false)
      .map((s) => s.key as string),
    copy: ((copy.data as any)?.value ?? {}) as Record<string, string>,
    partnerBanners: liveBanners as BusinessPartnerBanner[],
    categories: (categories.data ?? []) as BusinessCategory[],
    listings: ((listings.data ?? []) as any[]).map((l) => ({
      ...l,
      category_ids: ((l.categories ?? []) as any[]).map((c) => c.category_id).filter(Boolean),
    })) as BusinessListingPublic[],
    services: (services.data ?? []) as BusinessServicePublic[],
    events: (events.data ?? []) as BusinessEventPublic[],
    lanePicks,
  };
});



/** Full upcoming-events feed for the dedicated /business/events listing page. */
export const businessGetPublicEvents = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();

  const nowIso = new Date().toISOString();

  const [events, categories, copy] = await Promise.all([
    supabase
      .from("business_events")
      .select(
        "id,title_en,title_fi,description_en,description_fi,image_url,venue_name,destination,starts_at,ends_at,external_url,registration_url,price_text,is_featured,category_id",
      )
      .eq("status", "published")
      .is("archived_at", null)
      .or(`ends_at.gte.${nowIso},and(ends_at.is.null,starts_at.gte.${nowIso})`)
      .order("starts_at", { ascending: true })
      .limit(200),
    supabase
      .from("business_categories")
      .select("id,key,name_en,name_fi,icon,icon_url,image_url,sort_order")
      .eq("is_active", true)
      .is("archived_at", null)
      .order("sort_order", { ascending: true }),
    supabase.from("site_settings").select("value").eq("key", "business_home_copy").maybeSingle(),
  ]);

  return {
    events: (events.data ?? []) as BusinessEventPublic[],
    categories: (categories.data ?? []) as BusinessCategory[],
    // Admin-controlled copy (incl. the Submit event button settings).
    copy: ((copy.data as any)?.value ?? {}) as Record<string, string>,
  };
});

/**
 * Public read for a single category browsing page (`/business/category/$key`).
 *
 * Returns everything published under one taxonomy category — listings (via the
 * `business_listing_categories` join), services and upcoming events — plus the
 * full category rail so visitors can hop between categories. City filtering and
 * EN/FI copy selection happen client-side, exactly like the Business home page.
 */
export const businessGetPublicCategory = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => {
    const key = (d as any)?.key;
    if (typeof key !== "string" || !key || key.length > 64) throw new Error("Invalid category key");
    return { key };
  })
  .handler(async ({ data }) => {
    const supabase = publicClient();

    const nowIso = new Date().toISOString();

    const { data: categories } = await supabase
      .from("business_categories")
      .select("id,key,name_en,name_fi,description_en,description_fi,icon,icon_url,image_url,sort_order")
      .eq("is_active", true)
      .is("archived_at", null)
      .order("sort_order", { ascending: true });

    const all = (categories ?? []) as any[];
    const category = all.find((c) => c.key === data.key) ?? null;

    if (!category) {
      return {
        category: null,
        categories: all as BusinessCategory[],
        listings: [] as BusinessListingPublic[],
        services: [] as BusinessServicePublic[],
        events: [] as BusinessEventPublic[],
        lanePicks: {} as Record<string, string[]>,
      };
    }

    // Curated place picks (Business admin → Lanes). The category browsing page
    // needs these too — otherwise a lane curated by hand looks empty here even
    // though the picks show on the Business home page.
    const { data: lanePickRows } = await supabase
      .from("business_lane_picks")
      .select("lane_key,sort_order,place:places(id,slug,published)")
      .order("sort_order", { ascending: true });

    const lanePicks: Record<string, string[]> = {};
    for (const row of (lanePickRows ?? []) as any[]) {
      const place = row.place;
      if (!place || place.published === false) continue;
      const pkey = place.slug || place.id;
      if (!pkey) continue;
      (lanePicks[row.lane_key] ??= []).push(pkey);
    }

    const { data: joinRows } = await supabase
      .from("business_listing_categories")
      .select("listing_id")
      .eq("category_id", category.id);
    const listingIds = (joinRows ?? []).map((r: any) => r.listing_id).filter(Boolean);

    const [listings, services, events] = await Promise.all([
      listingIds.length
        ? supabase
            .from("business_listings")
            .select(
              "id,name,summary_en,summary_fi,description_en,description_fi,address,lat,lng,destinations,tags,images,links,contact,is_featured,sort_order,place:places(image_url)",
            )
            .eq("status", "published")
            .in("id", listingIds)
            .order("is_featured", { ascending: false })
            .order("sort_order", { ascending: true })
            .limit(200)
        : Promise.resolve({ data: [] as any[] }),
      supabase
        .from("business_services")
        .select(
          "id,category_id,name_en,name_fi,description_en,description_fi,image_url,destinations,links,is_featured",
        )
        .eq("status", "published")
        .eq("category_id", category.id)
        .order("is_featured", { ascending: false })
        .order("sort_order", { ascending: true })
        .limit(60),
      supabase
        .from("business_events")
        .select(
          "id,title_en,title_fi,description_en,description_fi,image_url,venue_name,destination,starts_at,ends_at,external_url,registration_url,price_text,is_featured,category_id",
        )
        .eq("status", "published")
        .eq("category_id", category.id)
        .is("archived_at", null)
        .or(`ends_at.gte.${nowIso},and(ends_at.is.null,starts_at.gte.${nowIso})`)
        .order("starts_at", { ascending: true })
        .limit(60),
    ]);

    return {
      category: category as BusinessCategory & {
        description_en: string | null;
        description_fi: string | null;
      },
      categories: all as BusinessCategory[],
      listings: (listings.data ?? []) as BusinessListingPublic[],
      services: (services.data ?? []) as BusinessServicePublic[],
      events: (events.data ?? []) as BusinessEventPublic[],
      lanePicks,
    };
  });
